import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  api,
  type EvalSummary,
  type GbmSummary,
  type HmmSummary,
  type RsfSummary,
} from "../api";

function fmt(v: number | null | undefined, digits = 3): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return v.toFixed(digits);
}

function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}

// Tier labels for held-out eval metrics. Thresholds are calibrated for
// trend-resumption detection on noisy financial data: "good" means the
// metric is genuinely strong, "suspect" flags suspiciously-perfect
// numbers that often indicate leakage, "thin" flags rows where the
// sample size is too small to draw conclusions from.
type Tier = "good" | "ok" | "weak" | "bad" | "suspect" | "thin";

function cIndexTier(v: number): Tier {
  if (v >= 0.9) return "suspect";
  if (v >= 0.8) return "good";
  if (v >= 0.7) return "ok";
  if (v >= 0.6) return "weak";
  return "bad";
}

function brierTier(v: number): Tier {
  if (v < 0.05) return "suspect";
  if (v < 0.15) return "good";
  if (v < 0.20) return "ok";
  if (v < 0.23) return "weak";
  return "bad";
}

function spearmanTier(v: number): Tier {
  if (v >= 0.7) return "suspect";
  if (v >= 0.5) return "good";
  if (v >= 0.3) return "ok";
  if (v >= 0.15) return "weak";
  return "bad";
}

function aurocTier(v: number): Tier {
  if (v >= 0.95) return "suspect";
  if (v >= 0.8) return "good";
  if (v >= 0.7) return "ok";
  if (v >= 0.6) return "weak";
  return "bad";
}

function calibrationTier(p_pred: number, p_emp: number, n: number): Tier {
  if (n < 6) return "thin";
  const gap = Math.abs(p_pred - p_emp);
  // Approximate binomial SE for p_emp at this n; one row's noise floor.
  const p = Math.max(0.05, Math.min(0.95, p_pred));
  const se = Math.sqrt((p * (1 - p)) / n);
  if (gap < Math.max(0.08, 1.5 * se)) return "good";
  if (gap < Math.max(0.15, 2.5 * se)) return "ok";
  if (gap < 0.25) return "weak";
  return "bad";
}

function sampleTier(n: number): Tier {
  if (n < 6) return "thin";
  if (n < 10) return "weak";
  return "good";
}

function tierClass(tier: Tier | null): string {
  return tier ? `tier-cell tier-${tier}` : "";
}

type LegendRow = { name: string; bands: { tier: Tier; label: string }[] };

const LEGEND_ROWS: Record<string, LegendRow> = {
  cIndex: {
    name: "C-index",
    bands: [
      { tier: "bad",     label: "< 0.6" },
      { tier: "weak",    label: "0.6 – 0.7" },
      { tier: "ok",      label: "0.7 – 0.8" },
      { tier: "good",    label: "0.8 – 0.9" },
      { tier: "suspect", label: "≥ 0.9" },
    ],
  },
  brier: {
    name: "Brier (lower is better)",
    bands: [
      { tier: "suspect", label: "< 0.05" },
      { tier: "good",    label: "0.05 – 0.15" },
      { tier: "ok",      label: "0.15 – 0.20" },
      { tier: "weak",    label: "0.20 – 0.23" },
      { tier: "bad",     label: "≥ 0.23" },
    ],
  },
  spearman: {
    name: "Spearman ρ",
    bands: [
      { tier: "bad",     label: "< 0.15" },
      { tier: "weak",    label: "0.15 – 0.3" },
      { tier: "ok",      label: "0.3 – 0.5" },
      { tier: "good",    label: "0.5 – 0.7" },
      { tier: "suspect", label: "≥ 0.7" },
    ],
  },
  auroc: {
    name: "AUROC",
    bands: [
      { tier: "bad",     label: "< 0.6" },
      { tier: "weak",    label: "0.6 – 0.7" },
      { tier: "ok",      label: "0.7 – 0.8" },
      { tier: "good",    label: "0.8 – 0.95" },
      { tier: "suspect", label: "≥ 0.95" },
    ],
  },
  sample: {
    name: "Sample size (n)",
    bands: [
      { tier: "thin", label: "n < 6  (too small to trust)" },
      { tier: "weak", label: "n < 10 (noisy)" },
      { tier: "good", label: "n ≥ 10" },
    ],
  },
  calibration: {
    name: "Calibration (p_emp vs p_pred)",
    bands: [
      { tier: "good", label: "within binomial noise" },
      { tier: "ok",   label: "small gap" },
      { tier: "weak", label: "gap < 25 pts" },
      { tier: "bad",  label: "gap ≥ 25 pts" },
    ],
  },
};

function TierLegend({ which }: { which: "rsf" | "gbm" }) {
  const keys = which === "rsf"
    ? ["cIndex", "brier", "calibration", "sample"]
    : ["spearman", "auroc", "sample"];
  const rows = keys.map((k) => LEGEND_ROWS[k]);
  return (
    <div className="tier-legend">
      <div className="tier-legend-title">Cell color guide</div>
      {rows.map((r) => (
        <div className="tier-legend-row" key={r.name}>
          <div className="tier-legend-name">{r.name}</div>
          <div className="tier-legend-bands">
            {r.bands.map((b, i) => (
              <span key={i} className={`tier-cell tier-${b.tier}`}>
                <strong>{b.tier}</strong> · {b.label}
              </span>
            ))}
          </div>
        </div>
      ))}
      <div className="tier-legend-note muted small">
        <strong>Suspect</strong> flags suspiciously-strong numbers that
        often indicate data leakage or overfitting, not real skill.
      </div>
    </div>
  );
}

type ModelTab = "hmm" | "rsf" | "gbm";
const MODEL_TABS: { id: ModelTab; label: string }[] = [
  { id: "hmm", label: "HMM" },
  { id: "rsf", label: "RSF" },
  { id: "gbm", label: "GBM" },
];

export default function ModelsView() {
  const q = useQuery({
    queryKey: ["models"],
    queryFn: () => api.models(),
    staleTime: 5 * 60_000,
  });

  const timeframes = q.data?.timeframes ?? [];
  const [activeTf, setActiveTf] = useState<string | null>(null);
  const [activeModel, setActiveModel] = useState<ModelTab>("hmm");
  const active = useMemo(() => {
    if (!timeframes.length) return null;
    const want = activeTf ?? timeframes[0].timeframe;
    return timeframes.find((t) => t.timeframe === want) ?? timeframes[0];
  }, [timeframes, activeTf]);

  if (q.isLoading) return <p className="muted">Loading model metadata…</p>;
  if (q.error) return <p className="muted">Failed to load models.</p>;
  if (!timeframes.length) return <p className="muted">No trained models found.</p>;

  return (
    <div>
      <div className="detail-header">
        <h2>Models</h2>
        <span style={{ flex: 1 }} />
      </div>

      <div className="model-tabs">
        {MODEL_TABS.map((m) => (
          <button
            key={m.id}
            className={m.id === activeModel ? "model-tab active" : "model-tab"}
            onClick={() => setActiveModel(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="tf-tabs">
        {timeframes.map((t) => (
          <button
            key={t.timeframe}
            className={t.timeframe === active?.timeframe ? "tf-tab active" : "tf-tab"}
            onClick={() => setActiveTf(t.timeframe)}
          >
            {t.timeframe}
          </button>
        ))}
      </div>

      {active && (
        <div className="model-page">
          {activeModel === "hmm" && <HmmPanel hmm={active.hmm} />}
          {activeModel === "rsf" && <RsfPanel rsf={active.rsf} ev={active.eval} />}
          {activeModel === "gbm" && <GbmPanel gbm={active.gbm} ev={active.eval} />}
          {activeModel === "rsf" && <TierLegend which="rsf" />}
          {activeModel === "gbm" && <TierLegend which="gbm" />}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="model-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function HmmPanel({ hmm }: { hmm: HmmSummary | null }) {
  if (!hmm) return <Section title="HMM"><p className="muted">No HMM artifact.</p></Section>;
  const stateColor = (name: string) => {
    switch (name) {
      case "bull": return "var(--good)";
      case "bear": return "var(--bad)";
      case "range": return "var(--accent)";
      default: return "var(--muted)";
    }
  };
  return (
    <Section title={`HMM — ${hmm.n_states} states, ${hmm.feature_names.length} features per bar`}>
      <p className="muted small" style={{ marginTop: 0 }}>
        Three unsupervised states (bull / bear / range) over signed per-bar
        features. The transition matrix shows how the regime evolves bar to bar;
        diagonals close to 1 mean the regime is sticky.
      </p>

      <h4 className="model-subhead">State means</h4>
      <div className="table-scroll">
        <table className="detail-table model-table">
          <thead>
            <tr>
              <th>state</th>
              {hmm.feature_names.map((f) => (
                <th key={f}>{f}</th>
              ))}
              <th>dwell (bars)</th>
            </tr>
          </thead>
          <tbody>
            {hmm.states.map((s) => (
              <tr key={s.raw_index}>
                <td>
                  <span
                    className="state-pill"
                    style={{ color: stateColor(s.name), borderColor: stateColor(s.name) }}
                  >
                    {s.name}
                  </span>
                </td>
                {s.means.map((m, i) => (
                  <td key={i}>
                    {fmt(m, 3)}
                    <span className="muted small"> ±{fmt(s.stddevs[i], 2)}</span>
                  </td>
                ))}
                <td>{fmt(s.expected_dwell_bars, 1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4 className="model-subhead">Transition matrix</h4>
      <p className="muted small" style={{ marginTop: 0 }}>
        Row = current state, column = next state.
      </p>
      <div className="table-scroll">
        <table className="detail-table model-table">
          <thead>
            <tr>
              <th>from \ to</th>
              {hmm.states.map((s) => (
                <th key={s.raw_index}>{s.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hmm.transition_matrix.map((row, i) => (
              <tr key={i}>
                <th>{hmm.states[i]?.name ?? `state${i}`}</th>
                {row.map((v, j) => (
                  <td
                    key={j}
                    style={{
                      background: `rgba(79, 158, 255, ${Math.min(0.55, v).toFixed(3)})`,
                    }}
                  >
                    {fmtPct(v, 1)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function RsfPanel({ rsf, ev }: { rsf: RsfSummary | null; ev: EvalSummary | null }) {
  if (!rsf) return <Section title="RSF (timing head)"><p className="muted">No RSF artifact.</p></Section>;
  return (
    <Section title="RSF — Random Survival Forest (timing head)">
      <table className="detail-table">
        <tbody>
          <tr><th>Trained at</th><td>{rsf.trained_at ?? "—"}</td></tr>
          <tr><th>Setups used</th><td>{rsf.n_rows ?? "—"}</td></tr>
          <tr><th>With-trend breakouts (events)</th><td>{rsf.n_events ?? "—"}</td></tr>
          <tr>
            <th>Feature count</th>
            <td>{rsf.features?.length ?? "—"}</td>
          </tr>
        </tbody>
      </table>

      <RsfEvalBlock ev={ev} />
    </Section>
  );
}

function RsfEvalBlock({ ev }: { ev: EvalSummary | null }) {
  if (!ev || !ev.rsf) {
    return (
      <>
        <h4 className="model-subhead">Held-out evaluation</h4>
        <p className="muted small">
          No held-out numbers yet. Run <code>./edge eval</code> to generate.
        </p>
      </>
    );
  }
  const briers = ev.rsf.integrated_brier ?? {};
  const horizonKeys = Object.keys(briers).filter((k) => k.startsWith("h"));
  return (
    <>
      <h4 className="model-subhead">Held-out evaluation</h4>
      <p className="muted small" style={{ marginTop: 0 }}>
        Time-blocked split, cutoff {ev.cutoff_date || "—"}, embargo {ev.embargo_bars}d.
        Train {ev.rsf.n_train} rows ({ev.rsf.n_events_train} events),
        test {ev.rsf.n_test} rows ({ev.rsf.n_events_test} events).
      </p>
      <table className="detail-table">
        <tbody>
          <tr>
            <th>C-index (held-out)</th>
            <td className={tierClass(typeof ev.rsf.c_index_test === "number" ? cIndexTier(ev.rsf.c_index_test) : null)}>
              {fmt(ev.rsf.c_index_test, 3)}
            </td>
          </tr>
          {horizonKeys.map((k) => {
            const v = briers[k];
            return (
              <tr key={k}>
                <th>Brier @ {k.replace(/^h/, "")} bars</th>
                <td className={tierClass(typeof v === "number" ? brierTier(v) : null)}>
                  {typeof v === "number" ? fmt(v, 3) : String(v)}
                </td>
              </tr>
            );
          })}
          {typeof briers.integrated_3_to_max === "number" && (
            <tr>
              <th>Integrated Brier (3 → max)</th>
              <td className={tierClass(brierTier(briers.integrated_3_to_max as number))}>
                {fmt(briers.integrated_3_to_max as number, 3)}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {ev.rsf.calibration.length > 0 && (
        <>
          <h4 className="model-subhead">Calibration deciles (p_pred → p_emp)</h4>
          <table className="detail-table">
            <thead>
              <tr><th>Decile</th><th>n</th><th>p_pred</th><th>p_emp</th></tr>
            </thead>
            <tbody>
              {ev.rsf.calibration.map((c) => (
                <tr key={c.decile}>
                  <th>{c.decile}</th>
                  <td className={tierClass(sampleTier(c.n))}>{c.n}</td>
                  <td>{fmtPct(c.p_pred_mean)}</td>
                  <td className={tierClass(calibrationTier(c.p_pred_mean, c.p_emp, c.n))}>
                    {fmtPct(c.p_emp)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {ev.notes && ev.notes.length > 0 && (
        <p className="muted" style={{ marginTop: 12 }}>{ev.notes.join(" · ")}</p>
      )}
    </>
  );
}

function GbmPanel({ gbm, ev }: { gbm: GbmSummary | null; ev: EvalSummary | null }) {
  if (!gbm) return <Section title="GBM (quality head)"><p className="muted">No GBM artifact.</p></Section>;
  const max = gbm.feature_importances?.[0]?.gain ?? 1;
  return (
    <Section title="GBM — LightGBM quality scorer">
      <table className="detail-table">
        <tbody>
          <tr><th>Trained at</th><td>{gbm.trained_at ?? "—"}</td></tr>
          <tr><th>Setups used</th><td>{gbm.n_rows ?? "—"}</td></tr>
          <tr><th>Target source</th><td>{gbm.target_source ?? "—"}</td></tr>
          <tr><th>Hand labels used</th><td>{gbm.hand_labels_used ?? 0}</td></tr>
          <tr><th>Best iteration</th><td>{gbm.best_iteration ?? "—"}</td></tr>
        </tbody>
      </table>

      <GbmEvalBlock ev={ev} />

      {gbm.feature_importances && gbm.feature_importances.length > 0 && (
        <>
          <h4 className="model-subhead">Feature importance (gain)</h4>
          <div className="importance-bars">
            {gbm.feature_importances.map((f) => (
              <div className="importance-row" key={f.name}>
                <div className="importance-name">{f.name}</div>
                <div className="importance-track">
                  <div
                    className="importance-bar"
                    style={{ width: `${Math.max(2, (f.gain / max) * 100)}%` }}
                  />
                </div>
                <div className="importance-value">{f.gain.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </Section>
  );
}

function GbmEvalBlock({ ev }: { ev: EvalSummary | null }) {
  if (!ev || !ev.gbm) {
    return (
      <>
        <h4 className="model-subhead">Held-out evaluation</h4>
        <p className="muted small">
          No held-out numbers yet. Run <code>./edge eval</code> to generate.
        </p>
      </>
    );
  }
  return (
    <>
      <h4 className="model-subhead">Held-out evaluation</h4>
      <p className="muted small" style={{ marginTop: 0 }}>
        Time-blocked split, cutoff {ev.cutoff_date || "—"}, embargo {ev.embargo_bars}d.
        Train {ev.gbm.n_train} rows, test {ev.gbm.n_test} rows.
      </p>
      <table className="detail-table">
        <tbody>
          <tr>
            <th>Spearman ρ</th>
            <td className={tierClass(typeof ev.gbm.spearman === "number" ? spearmanTier(ev.gbm.spearman) : null)}>
              {fmt(ev.gbm.spearman, 3)}
            </td>
          </tr>
          <tr>
            <th>AUROC</th>
            <td className={tierClass(typeof ev.gbm.auroc === "number" ? aurocTier(ev.gbm.auroc) : null)}>
              {fmt(ev.gbm.auroc, 3)}
            </td>
          </tr>
        </tbody>
      </table>

      {ev.gbm.decile_lift.length > 0 && (
        <>
          <h4 className="model-subhead">Decile lift (score → realized outcome)</h4>
          <table className="detail-table">
            <thead>
              <tr><th>Decile</th><th>n</th><th>mean score</th><th>mean outcome</th></tr>
            </thead>
            <tbody>
              {ev.gbm.decile_lift.map((d) => (
                <tr key={d.decile}>
                  <th>{d.decile}</th>
                  <td className={tierClass(sampleTier(d.n))}>{d.n}</td>
                  <td>{fmt(d.mean_score, 2)}</td>
                  <td>{fmtPct(d.mean_outcome)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {ev.notes && ev.notes.length > 0 && (
        <p className="muted" style={{ marginTop: 12 }}>{ev.notes.join(" · ")}</p>
      )}
    </>
  );
}
